import { setCookie, getCookie } from './utils/cookies.js';

/**
 * GDPR-Compliant Cookie Consent Banner
 * Minimalist design matching Halli/Osmo aesthetic
 */

class CookieConsent {
    constructor() {
        this.cookieName = 'cookie_consent';
        this.cookieExpiry = 365; // days
        this.init();
    }

    init() {
        // Check if consent already given
        const consent = getCookie(this.cookieName);
        if (!consent) {
            this.showBanner();
        } else {
            // If consent was given, load tracking scripts
            if (consent === 'accepted') {
                this.loadTrackingScripts();
            }
        }
    }

    showBanner() {
        // Create banner HTML
        const banner = document.createElement('div');
        banner.className = 'cookie-consent';
        banner.innerHTML = `
            <div class="cookie-consent__content">
                <p class="cookie-consent__text">
                    We use cookies to enhance your experience. You can manage your preferences or accept all. 
                    <a href="./cookie_policy.html" class="cookie-consent__link">Cookie Policy</a> · 
                    <a href="./imprint.html" class="cookie-consent__link">Imprint</a>
                </p>
                <div class="cookie-consent__actions">
                    <button class="cookie-consent__btn cookie-consent__btn--reject" data-action="reject">
                        Reject All
                    </button>
                    <button class="cookie-consent__btn cookie-consent__btn--settings" data-action="settings">
                        Settings
                    </button>
                    <button class="cookie-consent__btn cookie-consent__btn--accept" data-action="accept">
                        Accept All
                    </button>
                </div>
            </div>
        `;

        document.body.appendChild(banner);

        // Animate in with GSAP
        if (typeof gsap !== 'undefined') {
            gsap.from(banner, {
                y: 100,
                opacity: 0,
                duration: 0.8,
                ease: 'power2.out',
                delay: 1,
            });
        }

        // Add event listeners
        this.attachEventListeners(banner);
    }

    attachEventListeners(banner) {
        const buttons = banner.querySelectorAll('[data-action]');

        buttons.forEach((btn) => {
            btn.addEventListener('click', (e) => {
                const { action } = e.target.dataset;

                switch (action) {
                    case 'accept':
                        this.acceptAll(banner);
                        break;
                    case 'reject':
                        this.rejectAll(banner);
                        break;
                    case 'settings':
                        this.showSettings(banner);
                        break;
                }
            });
        });
    }

    acceptAll(banner) {
        setCookie(this.cookieName, 'accepted', this.cookieExpiry);
        this.loadTrackingScripts();
        this.hideBanner(banner);
    }

    rejectAll(banner) {
        setCookie(this.cookieName, 'rejected', this.cookieExpiry);
        this.hideBanner(banner);
    }

    showSettings(banner) {
        // Create settings modal
        const modal = document.createElement('div');
        modal.className = 'cookie-settings-modal';
        modal.innerHTML = `
            <div class="cookie-settings-modal__overlay"></div>
            <div class="cookie-settings-modal__content">
                <h2 class="cookie-settings-modal__title">Cookie Settings</h2>
                <p class="cookie-settings-modal__description">
                    Manage your cookie preferences. Essential cookies are always enabled.
                </p>
                
                <div class="cookie-settings-modal__options">
                    <div class="cookie-option">
                        <label class="cookie-option__label">
                            <input type="checkbox" checked disabled class="cookie-option__checkbox">
                            <span class="cookie-option__text">
                                <strong>Essential Cookies</strong>
                                <span class="cookie-option__desc">Required for basic site functionality</span>
                            </span>
                        </label>
                    </div>
                    
                    <div class="cookie-option">
                        <label class="cookie-option__label">
                            <input type="checkbox" id="analytics-cookies" class="cookie-option__checkbox">
                            <span class="cookie-option__text">
                                <strong>Analytics Cookies</strong>
                                <span class="cookie-option__desc">Help us understand how visitors use our site</span>
                            </span>
                        </label>
                    </div>
                </div>
                
                <div class="cookie-settings-modal__actions">
                    <button class="cookie-consent__btn cookie-consent__btn--reject" data-modal-action="close">
                        Cancel
                    </button>
                    <button class="cookie-consent__btn cookie-consent__btn--accept" data-modal-action="save">
                        Save Preferences
                    </button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Animate modal
        if (typeof gsap !== 'undefined') {
            gsap.from(modal.querySelector('.cookie-settings-modal__content'), {
                scale: 0.9,
                opacity: 0,
                duration: 0.4,
                ease: 'power2.out',
            });
        }

        // Modal event listeners
        modal
            .querySelector('[data-modal-action="close"]')
            .addEventListener('click', () => {
                this.hideModal(modal);
            });

        modal
            .querySelector('[data-modal-action="save"]')
            .addEventListener('click', () => {
                const analyticsEnabled = modal.querySelector('#analytics-cookies').checked;
                setCookie(
                    this.cookieName,
                    analyticsEnabled ? 'accepted' : 'rejected',
                    this.cookieExpiry,
                );

                if (analyticsEnabled) {
                    this.loadTrackingScripts();
                }

                this.hideModal(modal);
                this.hideBanner(banner);
            });

        modal
            .querySelector('.cookie-settings-modal__overlay')
            .addEventListener('click', () => {
                this.hideModal(modal);
            });
    }

    hideModal(modal) {
        if (typeof gsap !== 'undefined') {
            gsap.to(modal, {
                opacity: 0,
                duration: 0.3,
                onComplete: () => modal.remove(),
            });
        } else {
            modal.remove();
        }
    }

    hideBanner(banner) {
        if (typeof gsap !== 'undefined') {
            gsap.to(banner, {
                y: 100,
                opacity: 0,
                duration: 0.6,
                ease: 'power2.in',
                onComplete: () => banner.remove(),
            });
        } else {
            banner.remove();
        }
    }

    loadTrackingScripts() {
        // Placeholder for tracking scripts
        // Add Google Analytics, Hotjar, etc. here
        console.log('Tracking scripts loaded');

        // Example: Google Analytics
        // window.dataLayer = window.dataLayer || [];
        // function gtag(){dataLayer.push(arguments);}
        // gtag('js', new Date());
        // gtag('config', 'GA_MEASUREMENT_ID');
    }


}

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new CookieConsent());
} else {
    new CookieConsent();
}
